/**
 * The toasts on screen: top right, sliding in, each closing by itself after
 * its tone's dwell time (`DWELL`) or on its close button.
 *
 * Material UI's filled Alert, so every notification in the app looks the
 * same — confirmations, and the run watcher's started / completed / failed.
 */
import { useEffect, useState } from 'react';
import { Alert, Box, Slide } from '@mui/material';
import { useAppDispatch, useAppSelector } from '../store';
import {
	dismissToast,
	DWELL,
	type Toast,
	type ToastTone,
} from '../store/toastSlice';

const SEVERITY: Record<ToastTone, 'success' | 'error' | 'warning' | 'info'> = {
	ok: 'success',
	danger: 'error',
	warn: 'warning',
	info: 'info',
};

function ToastRow({ item }: { item: Toast }) {
	const dispatch = useAppDispatch();
	/* Closing slides the toast back out to the right, the way it came in; it
     leaves the store only once that slide has finished (`onExited`). */
	const [open, setOpen] = useState(true);

	/* The timer lives with the row rather than in the store: a reducer must stay
     pure, and unmounting the row is exactly when its timer should be cleared. */
	useEffect(() => {
		const timer = setTimeout(() => setOpen(false), DWELL[item.tone]);
		return () => clearTimeout(timer);
	}, [item.tone]);

	return (
		<Slide
			in={open}
			direction="left"
			appear
			onExited={() => dispatch(dismissToast(item.id))}
		>
			<Alert
				variant="filled"
				severity={SEVERITY[item.tone]}
				onClose={() => setOpen(false)}
				sx={{
					pointerEvents: 'auto',
					boxShadow: 3,
					alignItems: 'flex-start',
				}}
			>
				<Box className="b">{item.title}</Box>
				{item.message && <Box>{item.message}</Box>}
			</Alert>
		</Slide>
	);
}

export function Toaster() {
	const items = useAppSelector((state) => state.toast.items);

	return (
		/* polite, not assertive: a confirmation should not interrupt whatever
       the screen reader is already saying */
		<Box className="toasts" role="status" aria-live="polite">
			{/* Newest first: the latest toast sits at the top. */}
			{[...items].reverse().map((item) => (
				<ToastRow key={item.id} item={item} />
			))}
		</Box>
	);
}
